import { useEffect, useRef, useState } from "react";
import "../Styles/ContactForm.css";

const RECAPTCHA_SITE_KEY =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY || "6LesDhAtAAAAANEbdVN05l-brDOkYpdyHyUlvUSP";
const RECAPTCHA_ACTION = process.env.REACT_APP_RECAPTCHA_ACTION || "contact_form_submit";

function ContactForm({ contact, inboxEmail }) {
  const [submitState, setSubmitState] = useState("idle");
  const [captchaToken, setCaptchaToken] = useState("");
  const [captchaReady, setCaptchaReady] = useState(false);
  const firstInputRef = useRef(null);
  const recaptchaContainerRef = useRef(null);
  const recaptchaWidgetIdRef = useRef(null);

  const getRecaptchaApi = () => {
    if (
      window.grecaptcha?.enterprise?.ready &&
      window.grecaptcha?.enterprise?.render &&
      window.grecaptcha?.enterprise?.getResponse
    ) {
      return window.grecaptcha.enterprise;
    }

    return null;
  };

  const defaultContactForm = {
    title: "Tell Us About Your Project",
    subtitle: "Use the form to share your scope, timeline, or idea and our team will get back to you.",
    submit: "Send Inquiry",
    success: "Inquiry sent successfully.",
    error: "Something went wrong. Please try again.",
    sending: "Sending inquiry...",
    captchaLoading: "Loading spam protection...",
    captchaVerifying: "Verifying you're human...",
    captchaUnavailable: "Spam protection is temporarily unavailable. Please refresh and try again.",
    captchaFailed: "Spam protection check failed. Please try again.",
    captchaNotice: "This form is protected by reCAPTCHA Enterprise.",
    emailIntro: "New project inquiry from the website.",
    defaultSubject: "New project inquiry",
    fields: {
      name: "Name",
      email: contact.emailLabel,
      subject: "Subject",
      message: "Message",
    },
    placeholders: {
      name: "Your name",
      email: "you@company.com",
      subject: "Project scope",
      message: "Tell us what you need built.",
    },
  };

  useEffect(() => {
    if (!RECAPTCHA_SITE_KEY || typeof window === "undefined") {
      return undefined;
    }

    let isCancelled = false;
    let pollTimeoutId = 0;

    const renderCaptcha = () => {
      const recaptchaApi = getRecaptchaApi();

      if (
        isCancelled ||
        !recaptchaApi ||
        !recaptchaContainerRef.current ||
        recaptchaWidgetIdRef.current !== null
      ) {
        return;
      }

      recaptchaWidgetIdRef.current = recaptchaApi.render(recaptchaContainerRef.current, {
        sitekey: RECAPTCHA_SITE_KEY,
        action: RECAPTCHA_ACTION,
        size: window.matchMedia("(max-width: 480px)").matches ? "compact" : "normal",
        callback: (token) => {
          setCaptchaToken(token);
          setSubmitState("idle");
        },
        "expired-callback": () => {
          setCaptchaToken("");
        },
        "error-callback": () => {
          setCaptchaToken("");
          setSubmitState("captcha_failed");
        },
      });
      setCaptchaReady(true);
    };

    const waitForRenderableApi = () => {
      if (isCancelled) {
        return;
      }

      const recaptchaApi = getRecaptchaApi();
      if (recaptchaApi) {
        recaptchaApi.ready(renderCaptcha);
        return;
      }

      pollTimeoutId = window.setTimeout(waitForRenderableApi, 120);
    };

    const ensureCaptcha = () => {
      if (getRecaptchaApi()) {
        waitForRenderableApi();
        return;
      }

      const existingScript = document.querySelector('script[data-recaptcha="true"]');
      if (existingScript) {
        existingScript.addEventListener("load", waitForRenderableApi, { once: true });
        return;
      }

      const script = document.createElement("script");
      script.src = "https://www.google.com/recaptcha/enterprise.js";
      script.async = true;
      script.defer = true;
      script.dataset.recaptcha = "true";
      script.addEventListener("load", waitForRenderableApi, { once: true });
      document.head.appendChild(script);
    };

    const container = recaptchaContainerRef.current;
    let observer;

    if (container && "IntersectionObserver" in window) {
      observer = new IntersectionObserver(
        (entries) => {
          if (entries.some((entry) => entry.isIntersecting)) {
            ensureCaptcha();
            observer.disconnect();
          }
        },
        { rootMargin: "300px 0px" }
      );
      observer.observe(container);
    } else {
      ensureCaptcha();
    }

    return () => {
      isCancelled = true;
      observer?.disconnect();
      window.clearTimeout(pollTimeoutId);
    };
  }, []);

  const contactForm = {
    ...defaultContactForm,
    ...contact.form,
    fields: {
      ...defaultContactForm.fields,
      ...contact.form?.fields,
    },
    placeholders: {
      ...defaultContactForm.placeholders,
      ...contact.form?.placeholders,
    },
  };

  const statusMessage =
    submitState === "success"
      ? contactForm.success
      : submitState === "captcha_unavailable"
        ? contactForm.captchaUnavailable
        : submitState === "captcha_failed"
          ? contactForm.captchaFailed
          : submitState === "error"
            ? contactForm.error
            : "";

  const handleContactSubmit = async (event) => {
    event.preventDefault();
    const formElement = event.currentTarget;

    const formData = new FormData(formElement);
    const name = formData.get("name")?.toString().trim() ?? "";
    const email = formData.get("email")?.toString().trim() ?? "";
    const subject = formData.get("subject")?.toString().trim() ?? "";
    const message = formData.get("message")?.toString().trim() ?? "";

    const lines = [
      contactForm.emailIntro,
      "",
      `${contactForm.fields.name}: ${name}`,
      `${contactForm.fields.email}: ${email}`,
      `${contactForm.fields.subject}: ${subject || contactForm.defaultSubject}`,
      "",
      message,
    ];

    if (!captchaReady) {
      setSubmitState("captcha_unavailable");
      return;
    }

    if (!captchaToken) {
      setSubmitState("captcha_failed");
      return;
    }

    try {
      setSubmitState("sending");

      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name,
          email,
          subject: subject || contactForm.defaultSubject,
          message,
          body: lines.join("\n"),
          captchaToken,
          captchaAction: RECAPTCHA_ACTION,
        }),
      });

      if (!response.ok) {
        throw new Error("Contact request failed");
      }

      setSubmitState("success");
      setCaptchaToken("");
      formElement.reset();
      firstInputRef.current?.focus();
      const recaptchaApi = getRecaptchaApi();
      if (recaptchaApi && recaptchaWidgetIdRef.current !== null) {
        try {
          recaptchaApi.reset(recaptchaWidgetIdRef.current);
        } catch {
          // Some enterprise widget variants don't expose reset consistently.
        }
      }
    } catch {
      setSubmitState("error");
    }
  };

  return (
    <div className="SecondPart">
      <div className="contactSection" id="contact">
        <div className="contactIntro">
          <div>
            <h2>{contactForm.title}</h2>
            <p>{contactForm.subtitle}</p>

            <div className="contactQuickMeta">
              <p>
                <strong>{contact.emailLabel}:</strong> {inboxEmail}
              </p>
              {contact.phone && (
                <p>
                  <strong>{contact.phoneLabel}:</strong> {contact.phone}
                </p>
              )}
              {contact.location && <p>{contact.location}</p>}
            </div>

          </div>
        </div>

        <form className="contactForm" onSubmit={handleContactSubmit}>
          <div className="contactGrid">
            <label>
              <span>{contactForm.fields.name}</span>
              <input
                ref={firstInputRef}
                name="name"
                type="text"
                placeholder={contactForm.placeholders.name}
                required
              />
            </label>

            <label>
              <span>{contactForm.fields.email}</span>
              <input
                name="email"
                type="email"
                placeholder={contactForm.placeholders.email}
                required
              />
            </label>
          </div>

          <label>
            <span>{contactForm.fields.subject}</span>
            <input
              name="subject"
              type="text"
              placeholder={contactForm.placeholders.subject}
              required
            />
          </label>

          <label>
            <span>{contactForm.fields.message}</span>
            <textarea
              name="message"
              rows="5"
              placeholder={contactForm.placeholders.message}
              required
            />
          </label>

          <div className="contactFormFooter">
            <button className="GetStarted" type="submit" disabled={submitState === "sending"}>
              {submitState === "sending" ? contactForm.sending : contactForm.submit}
            </button>
            {statusMessage && <p role="status">{statusMessage}</p>}
          </div>

          <div className="contactCaptcha">
            <div ref={recaptchaContainerRef} />
            {!captchaReady && <p>{contactForm.captchaLoading}</p>}
            {!RECAPTCHA_SITE_KEY && <p>{contactForm.captchaUnavailable}</p>}
            {captchaReady && <p>{contactForm.captchaNotice}</p>}
          </div>
        </form>
      </div>
    </div>
  );
}

export default ContactForm;
